/**
 * QrCodeAssetService
 * Service for QR code asset management (icons, logos, frames, backgrounds)
 * Route: api/codes/qr-assets
 * Controller: QrCodeAssetsController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * QrCodeAssetService
 * Handles CRUD operations for QR code assets
 */
export class QrCodeAssetService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? QrCodeAssetService.DEFAULT_SERVICE_PATH,
            basePath: QrCodeAssetService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ OPERATIONS ====================
    /**
     * Get all QR code assets, optionally filtered by category
     * GET api/codes/qr-assets?category={category}
     */
    async getAll(category) {
        const response = await this.getAllRaw(category);
        return response.data ?? [];
    }
    /**
     * Get all assets with full response
     */
    getAllRaw(category) {
        let url = this.buildUrl();
        if (category) {
            url += `?category=${encodeURIComponent(category)}`;
        }
        return this.http.get(url);
    }
    /**
     * Get a single QR code asset by ID
     * GET api/codes/qr-assets/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'QR code asset not found');
        }
        return response.data;
    }
    /**
     * Get asset by ID with full response
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('get-by-id', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== WRITE OPERATIONS ====================
    /**
     * Create a new QR code asset
     * POST api/codes/qr-assets
     */
    async create(asset) {
        const response = await this.createRaw(asset);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create QR code asset');
        }
        return response.data;
    }
    /**
     * Create asset with full response
     */
    createRaw(asset) {
        const url = this.buildUrl();
        return this.http.post(url, asset);
    }
    /**
     * Update an existing QR code asset
     * PUT api/codes/qr-assets/{id}
     */
    async update(id, asset) {
        const response = await this.updateRaw(id, asset);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update QR code asset');
        }
        return response.data;
    }
    /**
     * Update asset with full response
     */
    updateRaw(id, asset) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, asset);
    }
    /**
     * Delete a QR code asset
     * DELETE api/codes/qr-assets/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        if (!response.success) {
            throw new Error(response.message || 'Failed to delete QR code asset');
        }
    }
    /**
     * Delete asset with full response
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
QrCodeAssetService.DEFAULT_SERVICE_PATH = 'code-generators';
QrCodeAssetService.BASE_PATH = 'api/codes/qr-assets';
//# sourceMappingURL=QrCodeAssetService.js.map